import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Globe, MapPin, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { CONTACT, SERVICES, FAQS } from "@/data/content";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["Under £1M", "£1M – £10M", "£10M – £50M", "£50M – £100M", "£100M+", "Prefer not to say"];
const INITIAL = { name: "", email: "", phone: "", company: "", service: "", budget: "", message: "" };

const inputCls =
  "w-full bg-void border border-zinc-800 px-4 py-3.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#C5A059] focus:outline-none transition-colors duration-300";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2";

function ContactForm() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent — Carlo will be in touch shortly.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please email us directly instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="border border-zinc-800/80 bg-void-2/30 p-8 sm:p-10">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="contact-name" className={labelCls}>Name *</label>
          <input id="contact-name" data-testid="contact-form-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
        </div>
        <div>
          <label htmlFor="contact-email" className={labelCls}>Email *</label>
          <input id="contact-email" data-testid="contact-form-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
        </div>
        <div>
          <label htmlFor="contact-phone" className={labelCls}>Phone</label>
          <input id="contact-phone" data-testid="contact-form-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+44 ..." />
        </div>
        <div>
          <label htmlFor="contact-company" className={labelCls}>Company</label>
          <input id="contact-company" data-testid="contact-form-company-input" value={form.company} onChange={set("company")} className={inputCls} placeholder="Organisation name" />
        </div>
        <div>
          <label htmlFor="contact-service" className={labelCls}>Service required</label>
          <select id="contact-service" data-testid="contact-form-service-select" value={form.service} onChange={set("service")} className={inputCls}>
            <option value="">Select a service</option>
            {SERVICES.map((s) => (
              <option key={s.id} value={s.title}>{s.title}</option>
            ))}
            <option value="General Enquiry">General enquiry</option>
          </select>
        </div>
        <div>
          <label htmlFor="contact-budget" className={labelCls}>Estimated project value</label>
          <select id="contact-budget" data-testid="contact-form-budget-select" value={form.budget} onChange={set("budget")} className={inputCls}>
            <option value="">Select a range</option>
            {BUDGETS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-6">
        <label htmlFor="contact-message" className={labelCls}>About your project *</label>
        <textarea
          id="contact-message"
          data-testid="contact-form-message-textarea"
          required
          rows={5}
          value={form.message}
          onChange={set("message")}
          className={`${inputCls} resize-none`}
          placeholder="Tell us where the project is, what stage it's at, and what support you're looking for."
        />
      </div>
      <button
        type="submit"
        data-testid="contact-form-submit-button"
        disabled={sending}
        className="mt-8 inline-flex items-center gap-3 bg-zinc-50 text-zinc-950 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
      >
        {sending && <Loader2 className="w-4 h-4 animate-spin" />}
        {sending ? "Sending..." : "Send enquiry"}
      </button>
    </form>
  );
}

function DirectDetails() {
  return (
    <div className="space-y-6">
      <Reveal>
        <div className="border border-zinc-800/80 bg-void-2/30 p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-6">Direct details</p>
          <ul className="space-y-5 text-sm">
            <li>
              <a href={`mailto:${CONTACT.emailInfo}`} className="flex items-start gap-3 text-zinc-300 hover:text-zinc-50 transition-colors">
                <Mail className="w-4 h-4 mt-0.5 text-[#C5A059] shrink-0" /> {CONTACT.emailInfo}
              </a>
            </li>
            <li>
              <a href={CONTACT.phoneHref} data-testid="contact-direct-phone" className="flex items-start gap-3 text-zinc-300 hover:text-zinc-50 transition-colors">
                <Phone className="w-4 h-4 mt-0.5 text-[#C5A059] shrink-0" /> {CONTACT.phone}
              </a>
            </li>
            <li>
              <a href={CONTACT.webHref} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-zinc-300 hover:text-zinc-50 transition-colors">
                <Globe className="w-4 h-4 mt-0.5 text-[#C5A059] shrink-0" /> {CONTACT.web}
              </a>
            </li>
          </ul>
        </div>
      </Reveal>
      <Reveal delay={0.12}>
        <div className="relative border border-zinc-800/80 overflow-hidden blueprint-grid bg-void-2/30 p-8" data-testid="coverage-card">
          <MapPin className="w-5 h-5 text-[#C5A059]" />
          <p className="mt-4 font-display text-lg font-bold text-zinc-100">UK &amp; Ireland coverage</p>
          <p className="mt-3 text-sm text-zinc-400 font-light leading-relaxed">
            Current and recent schemes across Edinburgh, East Lothian, Cambridge and London Stansted — and wherever
            the next project takes us.
          </p>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
            Fox Project Management Consultancy Ltd
          </p>
        </div>
      </Reveal>
    </div>
  );
}

function FAQ() {
  return (
    <section className="max-w-7xl mx-auto px-6 pb-24 sm:pb-32">
      <Reveal>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
          <span className="w-10 h-px bg-[#C5A059]" /> Common questions
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-10 border-t border-zinc-800/60" data-testid="contact-faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-zinc-800/60">
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="text-left font-display text-base sm:text-lg font-bold text-zinc-100 hover:text-[#C5A059] hover:no-underline py-6 transition-colors"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-zinc-400 font-light leading-relaxed pb-6 max-w-3xl">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>
  );
}

export default function Contact() {
  return (
    <>
      <PageHeader
        overline="Contact"
        lines={["Start the", "conversation"]}
        intro="Tell us where your project is heading. Every enquiry lands directly with the director — expect a personal reply, not a ticket number."
        testid="contact-headline"
      />
      <section className="max-w-7xl mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-5 gap-10">
        <Reveal className="lg:col-span-3">
          <ContactForm />
        </Reveal>
        <div className="lg:col-span-2">
          <DirectDetails />
        </div>
      </section>
      <FAQ />
    </>
  );
}
